import { motion } from "framer-motion";
import { useAnimateOnScroll } from "Hooks/useAnimateOnScroll";
import { StyledButton as Button } from "Components/StyledButton";
import {
  CheckCircle,
  Target,
  CreditCard,
  FileText,
  Users,
  ArrowRight,
} from "lucide-react";
import { fadeIn, itemFadeIn, staggerContainer } from "../Common/fadeStyles";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import "./styles.css";

export const HeroSection = () => {
  const [heroRef, heroControls] = useAnimateOnScroll(0.1);
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden bg-white min-h-[90vh] flex flex-col items-center w-full lg:pt-8">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-green-50"></div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#80E280] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#80E280] rounded-full blur-3xl"></div>
      </div>

      <motion.div
        // @ts-ignore
        ref={heroRef}
        initial="hidden"
        // @ts-ignore
        animate={heroControls}
        variants={fadeIn}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-15 w-full"
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-gray-900">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6 text-left">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="block"
              >
                College,
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="block text-[#80E280]"
              >
                Finance,
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="block"
              >
                Immigration & Career
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-xl md:text-2xl mb-6 text-gray-600 leading-relaxed text-left font-medium"
            >
              Everything you need in one place.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mb-4 p-4 bg-gray-50 rounded-lg border-l-4 border-[#80E280] text-left hero-description"
            >
              <p className="text-gray-700 text-sm md:text-base text-justify mb-0">
                <span className="font-semibold text-gray-900">
                  Built by MIT graduates, for ambitious students.
                </span>{" "}
                Our team understands the challenges of studying abroad and have
                created the most comprehensive platform to guide your journey.
              </p>
            </motion.div>

            {/* Key features highlight */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="flex flex-wrap gap-4 text-sm text-gray-600"
            >
              <div className="flex items-center text-left text-base">
                <CheckCircle className="w-4 h-4 text-[#80E280] mr-2" />
                AI-powered matching
              </div>
              <div className="flex items-center text-left text-base">
                <CheckCircle className="w-4 h-4 text-[#80E280] mr-2" />
                No hidden fees
              </div>
              <div className="flex items-center text-left text-base">
                <CheckCircle className="w-4 h-4 text-[#80E280] mr-2" />
                Expert guidance
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.8, type: "spring" }}
            className="relative"
          >
            {/* Main dashboard mockup */}
            <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-[#80E280] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">OG</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900 text-left">
                      OneGrad Journey
                    </span>
                    <div className="text-xs text-gray-500 text-left">
                      MIT Startup
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
              </div>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="space-y-4"
              >
                {[
                  {
                    icon: Target,
                    title: "College Matching",
                    desc: "95% compatibility found",
                    color: "bg-blue-100 text-blue-600",
                    target: ROUTES.COLLEGE_FINDER,
                  },
                  {
                    icon: CreditCard,
                    title: "Finance Planning",
                    desc: "$45K funding secured",
                    color: "bg-green-100 text-green-600",
                    target: ROUTES.COLLEGE_FINDER,
                  },
                  {
                    icon: FileText,
                    title: "Immigration Status",
                    desc: "Visa approved ✓",
                    color: "bg-purple-100 text-purple-600",
                    target: ROUTES.COLLEGE_FINDER,
                  },
                  {
                    icon: Users,
                    title: "Career Guidance",
                    desc: "Job offers received",
                    color: "bg-orange-100 text-orange-600",
                    target: ROUTES.COLLEGE_FINDER,
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemFadeIn}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => {
                      navigate(item.target);
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center`}
                      >
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm text-left">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-xs text-left">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Floating success indicators */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut",
              }}
              className="absolute -top-6 -right-2 md:-right-6 bg-[#80E280] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
            >
              🎉 Accepted!
            </motion.div>

            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut",
              }}
              className="absolute -bottom-4 -left-2 md:-left-4 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
            >
              💰saved
            </motion.div>

            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut",
              }}
              className="absolute top-1/2 -left-2 md:-left-8  bg-white border border-gray-200 px-3 py-2 rounded-full text-xs font-semibold shadow-lg"
            >
              ✈️ Visa Ready
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1 }}
      >
        <Button
          onClick={() => {
            navigate(ROUTES.COLLEGE_FINDER);
          }}
          size="lg"
          className="bg-[#80E280] hover:bg-[#70D270] text-lg px-6 md:px-12 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg rounded-lg text-black font-semibold text-lg md:text-xl"
        >
          Get your colleges, it's Free! 🎓
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 5 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="flex items-center justify-center mb-4 pt-0 lg:pt-4"
      >
        <p className="text-lg font-bold mb-0">
          An <span className="text-[#750014]">MIT</span> startup
        </p>
      </motion.div>
    </section>
  );
};
